<script>
import FacilitySelect from "@/views/orders/components/FacilitySelect.vue";
import FormBlock from "@/components/form/FormBlock.vue";
import OmniOmniTextarea from "@/components/omni/OmniTextarea.vue";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin.ts";
import ValidationMixin from "@/mixins/validation.mixin.ts";
import BaseView from "@/mixins/BaseView.vue";
import OrderStopStatusBadge from "@/components/statuses/OrderStopStatus.vue";
import OrderStopStateBadge from "@/components/statuses/OrderStopState.vue";
import {
  OrderFreightState as FreightState,
  OrderFreightTypeObject,
  OrderStopShortTypeMap,
  OrderStopState as StopState,
  OrderStopStatus as StopStatus,
  OrderStopStatus,
  OrderStopType,
} from "@/constants/order.ts";
import StopDateRange from "@/views/orders/components/StopDateRange.vue";
import AlertMessage from "@/components/messages/AlertMessage.vue";
import OrderFreightState from "@/views/orders/edit/components/OrderFreightState.vue";
import { generateId } from "@/utils/generateId.ts";

export default {
  name: "OrderEditStops",
  components: {
    OrderFreightState,
    AlertMessage,
    StopDateRange,
    OrderStopStatus: OrderStopStatusBadge,
    OrderStopState: OrderStopStateBadge,
    OmniTextarea: OmniOmniTextarea,
    FormBlock,
    FacilitySelect,
  },
  mixins: [BaseView, OrdersMixin, ValidationMixin],
  props: {
    originalOrder: Object,
    modelValue: Object,
  },
  emits: ["update:modelValue", "updateDeliveryFreights"],
  data() {
    return {
      originalFreights: [],
    };
  },
  computed: {
    StopState() {
      return StopState;
    },
    order: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    stops: {
      get() {
        let pickUpKey = 0,
          deliveryKey = 0;
        const orderStops = this.order.orderStops;

        return orderStops
          .sort((a, b) => a.position - b.position)
          .map((orderStop) => {
            let key;
            if (orderStop.type === this.STOP_PICK_UP) {
              key = ++pickUpKey;
            } else if (orderStop.type === this.STOP_DELIVERY) {
              key = ++deliveryKey;
            }
            orderStop.$number = key;
            if (orderStop.time2From || orderStop.time2To) {
              orderStop.$showSecondDate = true;
            }
            return orderStop;
          });
      },
      set(value) {
        this.order.orderStops = value;
      },
    },
    freights() {
      return this.order.freights.map((freight) => {
        const freightPickupStop = this.stops.find(
          (s) => s.type === OrderStopType.pickup && s.freights.includes(freight.id),
        );

        // TODO: заменить логику калькуляции номера фрейта на useFreightOrdinals когда сделаю миграции на Vue3
        const freightIndexByStop = freightPickupStop?.freights.indexOf(freight.id) + 1;
        const typeToPrint = OrderFreightTypeObject[freight.type];
        freight.$number = `#${freightPickupStop?.$number ?? ""}-${freightIndexByStop}`;

        if (freightPickupStop) {
          const parts = [];
          parts.push(`Freight ${freight.$number}`);

          const details = [];

          const pickupKeyPart = `PU#${freightPickupStop.$number}`;
          details.push(pickupKeyPart);

          if (freightPickupStop.facility?.address) {
            details.push(`(${freightPickupStop.facility.address})`);
          }

          if (freight.quantity) {
            details.push(`${freight.quantity} ${typeToPrint}`);
          }

          if (freight.weight) {
            details.push(`${freight.weight} lbs`);
          }

          parts.push(details.join(", "));

          freight.$name = parts.join(", ");
        }

        freight.$usedIn = this.stops
          .filter((s) => s.type === OrderStopType.delivery && !s.isDeleted && s.freights.includes(freight.id))
          .map((s) => s.$number);
        return freight;
      });
    },
    facilities() {
      return this.order.orderStops
        .flatMap((orderStop) => {
          if (!orderStop.facility) {
            return null;
          }

          return {
            ...orderStop.facility,
            stopShortInfo: `${OrderStopShortTypeMap[orderStop.type]}#${orderStop.$number}`,
          };
        })
        .filter(Boolean);
    },
  },
  watch: {
    freights: {
      handler() {
        if (this.originalFreights.length === 0 && this.originalOrder) {
          this.originalFreights = this.freights.map((f) => ({ ...f }));
        }
        for (const freight of this.freights) {
          if (freight.length === "") freight.length = null;
          if (freight.width === "") freight.width = null;
          if (freight.height === "") freight.height = null;

          const originalFreight = this.originalFreights.find((f) => f.id === freight.id);
          if (originalFreight?.state === FreightState.updated) continue;
          if (originalFreight) {
            freight.state = this.compareFreights(freight, originalFreight) ? freight.state : FreightState.updated;
          } else {
            freight.state = FreightState.new;
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    compareFreights(freight1, freight2) {
      return (
        freight1.type === freight2.type &&
        freight1.stackable === freight2.stackable &&
        parseInt(freight1.quantity) === parseInt(freight2.quantity) &&
        parseInt(freight1.weight) === parseInt(freight2.weight)
      );
    },
    getStopFreights(stop) {
      return stop.freights
        .map((freightId) => this.freights.find((f) => f.id === freightId))
        .filter((f) => !(!this.order.tripId && f?.isDeleted));
    },
    getStopPallets(stop) {
      return this.getStopFreights(stop)
        .filter((f) => f.type === this.freightTypePallets)
        .map((f) => f.quantity || 0)
        .reduce((a, b) => parseInt(a) + parseInt(b), 0);
    },
    getStopPieces(stop) {
      return this.getStopFreights(stop)
        .filter((f) => f.type === this.freightTypePieces)
        .map((f) => f.quantity || 0)
        .reduce((a, b) => parseInt(a) + parseInt(b), 0);
    },
    getStopWeight(stop) {
      return this.getStopFreights(stop)
        .map((f) => f.weight || 0)
        .reduce((a, b) => parseInt(a) + parseInt(b), 0);
    },
    isStopDeletable(stop) {
      return (
        !stop.isDeleted &&
        stop.status === StopStatus.notStarted &&
        this.stops.filter((s) => s.type === stop.type && !s.isDeleted).length > 1
      );
    },
    async addFreight(stop) {
      const id = generateId();
      const freight = {
        id: id,
        type: this.freightTypePallets,
        quantity: 1,
        weight: 1,
        stackable: false,
        state: FreightState.new,
      };

      this.order.freights.push(freight);
      stop.freights.push(freight.id);

      this.$emit("updateDeliveryFreights");
    },
    removeFreight(freight) {
      if (
        !this.order.tripId ||
        (this.order.tripId && !(this.originalFreights.findIndex((f) => f.id === freight.id) > -1))
      ) {
        this.order = {
          ...this.order,
          freights: this.order.freights.filter((f) => f.id !== freight.id),
          orderStops: this.order.orderStops.map((stop) => {
            const updatedFreights = stop.freights.filter((id) => id !== freight.id);
            return {
              ...stop,
              freights: updatedFreights,
            };
          }),
        };
      } else {
        freight.isDeleted = true;
      }

      this.$emit("updateDeliveryFreights");
    },
    async addStop(stopType) {
      const stop = {
        id: generateId(),
        type: stopType,
        state: StopState.new,
        status: StopStatus.notStarted,
        freights: [],
        note: "",
        timeType: "f",
        timeFrom: null,
        timeTo: null,
        time2Type: "f",
        time2From: null,
        time2To: null,
        isDeleted: false,
        position: (Math.max(...this.stops.map((s) => s.position)) || 0) + 1,
      };

      if (stopType === this.STOP_PICK_UP) {
        await this.addFreight(stop);

        const nextStopIndex = this.order.orderStops.findIndex((s) => !s.status || s.status === "new");

        if (nextStopIndex !== -1) {
          this.order.orderStops.forEach((orderStop, index) => {
            if (index >= nextStopIndex) {
              orderStop.position++;
            }
          });

          stop.position = this.order.orderStops[nextStopIndex].position - 1;
        }
      }

      this.order.orderStops.push(stop);

      this.$emit("updateDeliveryFreights");
    },
    deleteStop(stop) {
      const isDeletable = this.isStopDeletable(stop);
      const isConfirmed = confirm("Are you sure to delete stop?");
      if (!isDeletable || !isConfirmed) {
        return;
      }

      const isNewStop = stop.state === StopState.new;
      const isPickup = stop.type === OrderStopType.pickup;

      let updatedOrderStops = [...this.order.orderStops];
      let updatedFreights = [...this.order.freights];

      if (isNewStop) {
        updatedOrderStops = updatedOrderStops.filter((s) => s.id !== stop.id);

        if (isPickup) {
          updatedFreights = updatedFreights.filter((freight) => !stop.freights.includes(freight.id));
        }
      } else {
        if (isPickup) {
          updatedFreights = updatedFreights.map((freight) =>
            stop.freights.includes(freight.id) ? { ...freight, isDeleted: true } : freight,
          );
        }

        updatedOrderStops = updatedOrderStops.map((s) => (s.id === stop.id ? { ...s, isDeleted: true } : s));
      }

      this.order = {
        ...this.order,
        orderStops: updatedOrderStops,
        freights: updatedFreights,
      };

      this.$emit("updateDeliveryFreights");
    },
    getFreightsForStop(stop, index) {
      const pickedBeforeFreights = this.stops
        .slice(0, index)
        .filter((s) => s.type === OrderStopType.pickup)
        .flatMap((s) => s.freights);
      return this.freights
        .filter((f) => pickedBeforeFreights.includes(f.id) && !(!this.order.tripId && f.isDeleted))
        .map((f) => {
          const usedIn = (f.$usedIn || []).filter((f) => f !== stop.$number);
          const suffix =
            usedIn.length > 0
              ? "Freight already selected at " + usedIn.map((i) => "Delivery#" + i).join(", ") + " stop(s)"
              : null;
          const disabled = this.isFreightOptionDisabled(stop, f);
          return {
            ...f,
            suffix,
            disabled,
          };
        });
    },
    isStopEditable(stop) {
      return (!this.order.id || StopStatus.notStarted === stop.status) && !stop.isDeleted;
    },
    isFreightEditable(stop, freight = null) {
      if (!this.order.id) {
        return true;
      }

      const hasEditableStopStatus = [OrderStopStatus.notStarted, OrderStopStatus.inProgress].includes(stop.status);

      if (!freight) {
        return hasEditableStopStatus;
      }

      return hasEditableStopStatus && !freight.isDeleted;
    },
    isFreightOptionDisabled(stop, freight) {
      const originalStop = this.originalOrder?.orderStops.find((s) => s.id === stop.id);

      if (!originalStop || !this.order.tripId) {
        return false;
      }

      const wasSelected = originalStop.freights.includes(freight.id);

      return wasSelected && !freight.isDeleted;
    },
    getSavedStop(stop) {
      return this.originalOrder?.orderStops.find((s) => s.id === stop.id);
    },
    isStopSaved(stop) {
      return !!this.getSavedStop(stop);
    },
    isStopUpdated(stop) {
      if (!this.originalOrder || !this.originalOrder.tripId) return false;
      if (stop.state === StopState.updated) return true;

      const originalStop = this.getSavedStop(stop);
      if (!originalStop) {
        return false;
      }
      const freights = this.getStopFreights(stop);
      return (
        originalStop.facilityId !== stop.facilityId ||
        freights.some((f) => f?.state !== FreightState.normal || f.isDeleted)
      );
    },
    getStopDeletedFreights(stop) {
      return stop.freights
        .map((freightId) => this.freights.find((f) => f.id === freightId))
        .filter((f) => f?.isDeleted);
    },
    facilityUpdated(stop, facility) {
      stop.timezone = facility.timezone;
      stop.facility = facility;
    },
    getFreightStateClass(freight) {
      if (!this.order.tripId || !this.order.id || !freight) {
        return {};
      }
      return {
        "freight--new": freight.state === FreightState.new,
        "freight--updated": freight.state === FreightState.updated && !freight.isDeleted,
        "freight--deleted": freight.isDeleted,
      };
    },
    moveUpBlocked(stop) {
      const stopIndex = this.stops.findIndex((s) => s.id === stop.id);
      const previousStopIndex = stopIndex - 1;
      const previousStop = this.stops[previousStopIndex];
      const result = {
        blocked: false,
        message: null,
      };
      const isLastStop = stopIndex === this.stops.length - 1;

      if (!previousStop) {
        result.blocked = true;
        result.message = "The stop is first in order";
        return result;
      }
      if (stopIndex === 1 && stop.type === OrderStopType.delivery) {
        result.blocked = true;
        result.message = "Delivery can't be the first stop in a order";
        return result;
      }
      if (this.isStopSaved(stop) && this.isStopSaved(previousStop)) {
        result.blocked = true;
        result.message = "Both stops have been already saved (You can change position only for not saved stops)";
        return result;
      }
      if (
        stop.type === OrderStopType.delivery &&
        previousStop.type === OrderStopType.pickup &&
        stop.freights.filter((freightId) => previousStop.freights.includes(freightId)).length > 0
      ) {
        result.blocked = true;
        result.message = "Freight(s) is/are at the both stops";
        return result;
      }
      if ((stop.status && stop.status !== "new") || (previousStop.status && previousStop.status !== "new")) {
        result.blocked = true;
        result.message = "Both of the stops must have 'Not started' status";
        return result;
      }

      if (stop.type === OrderStopType.delivery && previousStop.type === OrderStopType.pickup && isLastStop) {
        result.blocked = true;
        result.message = "Pickup can't be the the last stop in a order.";
        return result;
      }

      return result;
    },
    moveDownBlocked(stop) {
      const stopIndex = this.stops.findIndex((s) => s.id === stop.id);
      const nextStopIndex = stopIndex + 1;
      const nextStop = this.stops[nextStopIndex];
      const isPenultimateStop = stopIndex + 2 === this.stops.length;
      const result = {
        blocked: false,
        message: null,
      };

      if (!nextStop) {
        result.blocked = true;
        result.message = "The stop is last in order";
        return result;
      }

      if (stopIndex === 0 && stop.type === OrderStopType.pickup && nextStop.type === OrderStopType.delivery) {
        result.blocked = true;
        result.message = "Delivery can't be the first stop in a order";
        return result;
      }

      if (isPenultimateStop && stop.type === OrderStopType.pickup && nextStop.type === OrderStopType.delivery) {
        result.blocked = true;
        result.message = "Pickup can't be the the last stop in a order.";
        return result;
      }
      if (this.isStopSaved(stop) && this.isStopSaved(nextStop)) {
        result.blocked = true;
        result.message = "Both stops have been already saved (You can change position only for not saved stops)";
        return result;
      }
      if (
        stop.type === OrderStopType.pickup &&
        nextStop.type === OrderStopType.delivery &&
        stop.freights.filter((freightId) => nextStop.freights.includes(freightId)).length > 0
      ) {
        result.blocked = true;
        result.message = "Freight(s) is/are at the both stops";
        return result;
      }
      if ((stop.status && stop.status !== "new") || (nextStop.status && nextStop.status !== "new")) {
        result.blocked = true;
        result.message = "Both of the stops must have 'Not started' status";
        return result;
      }
      return result;
    },
    moveUp(stop) {
      const stopIndex = this.stops.findIndex((s) => s.id === stop.id);
      const previousStopIndex = stopIndex - 1;
      const previousStop = this.stops[previousStopIndex];
      [previousStop.position, stop.position] = [stop.position, previousStop.position];
    },
    moveDown(stop) {
      const stopIndex = this.stops.findIndex((s) => s.id === stop.id);
      const nextStopIndex = stopIndex + 1;
      const nextStop = this.stops[nextStopIndex];
      [nextStop.position, stop.position] = [stop.position, nextStop.position];
    },
    isAllFreightDeleted(stop) {
      const stopFreights = this.freights.filter((f) => stop.freights.includes(f.id));

      if (!stopFreights.length) {
        return false;
      }

      return stopFreights.every((freight) => freight.isDeleted);
    },
    isFreightSelectedOnDelivery(freight) {
      return (
        this.order.orderStops.filter((stop) => {
          return !stop.isDeleted && stop.type === OrderStopType.delivery && stop.freights.includes(freight.id);
        }).length > 0
      );
    },
  },
};
</script>

<template>
  <div class="mb-4">
    <div class="d-flex align-center justify-space-between mb-4">
      <div class="font-size-16 text-grey-darken-3 font-weight-medium">Stops</div>
      <div class="d-flex align-center">
        <v-btn
          class="text-uppercase text-green font-size-12 font-weight-600 pr-2 pl-2"
          color="green"
          height="28"
          variant="outlined"
          @click="addStop(STOP_PICK_UP)"
        >
          <v-icon class="mr-2" color="green" small> mdi-package-variant-closed</v-icon>
          Add pick up
        </v-btn>

        <v-btn
          class="text-uppercase text-red text--accent-2 font-size-12 font-weight-600 pr-2 pl-2 ml-4"
          color="red-accent-2"
          height="28"
          variant="outlined"
          @click="addStop(STOP_DELIVERY)"
        >
          <v-icon class="mr-2" color="red-accent-2" size="small"> mdi-map-marker-radius-outline</v-icon>
          Add delivery
        </v-btn>
      </div>
    </div>

    <template v-for="(stop, index) in stops" :key="stop.id">
      <div>
        <form-block
          :class="{
            'v-field--input': stop.$error || stop.freights?.some((f) => f.$error) || stop.$timeError,
          }"
          class="form-block"
        >
          <div class="d-flex align-center justify-space-between mb-4">
            <div :class="{ 'text-grey-lighten-1': stop.isDeleted }" class="d-flex align-center">
              <v-icon
                v-if="stop.type === STOP_PICK_UP"
                :color="stop.isDeleted ? 'grey-lighten-1' : 'green'"
                class="mr-2"
                size="20"
              >
                mdi-package-variant-closed
              </v-icon>
              <v-icon
                v-if="stop.type === STOP_DELIVERY"
                :color="stop.isDeleted ? 'grey-lighten-1' : 'red accent-2'"
                class="mr-2"
                size="20"
              >
                mdi-map-marker-radius-outline
              </v-icon>
              <span class="font-size-11 mr-1">Stop {{ index + 1 }} - </span>
              <span v-if="stop.type === STOP_PICK_UP" class="text-caption font-weight-600">
                Pick up #{{ stop.$number }}</span
              >
              <span v-if="stop.type === STOP_DELIVERY" class="text-caption font-weight-600">
                Delivery #{{ stop.$number }}</span
              >
              <order-stop-status v-if="order.id" :stop="stop" class="ml-2" hide-edge-statuses></order-stop-status>
              <order-stop-state
                v-if="order.id && order.tripId"
                :state="isStopUpdated(stop) ? StopState.updated : null"
                :stop="stop"
                class="ml-2"
              ></order-stop-state>
            </div>
            <div class="d-flex align-center">
              <v-btn v-if="isStopDeletable(stop)" class="delete-btn pa-0 mr-4" variant="text" @click="deleteStop(stop)">
                <v-icon class="mr-1" color="red-darken-1" size="20">mdi-delete-outline</v-icon>
                <span class="text-red-darken-1 text-uppercase">Delete stop</span>
              </v-btn>
              <div class="d-flex flex-column">
                <v-tooltip :disabled="!moveUpBlocked(stop).message" location="top">
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-btn
                        :disabled="moveUpBlocked(stop).blocked"
                        icon="mdi-chevron-up"
                        size="xs"
                        variant="plain"
                        @click="moveUp(stop)"
                      ></v-btn>
                    </div>
                  </template>
                  {{ moveUpBlocked(stop).message }}
                </v-tooltip>

                <v-tooltip :disabled="!moveDownBlocked(stop).message" location="top">
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-btn
                        :disabled="moveDownBlocked(stop).blocked"
                        icon="mdi-chevron-down"
                        size="xs"
                        variant="plain"
                        @click="moveDown(stop)"
                      ></v-btn>
                    </div>
                  </template>
                  {{ moveDownBlocked(stop).message }}
                </v-tooltip>
              </div>
            </div>
          </div>

          <v-row>
            <v-col lg="4" sm="12">
              <facility-select
                v-model="stop.facilityId"
                :header-facilities="facilities"
                :readonly="!isStopEditable(stop)"
                :rules="[requiredValidator]"
                coordinates-tooltip="Coordinates for calculating loaded miles"
                editable
                header-facilities-divider-text="Used at stops:"
                label="Facility"
                @update="(f) => facilityUpdated(stop, f)"
              />
            </v-col>
            <v-col lg="8" sm="12">
              <div class="stops-right__top">
                <stop-date-range v-model="stops[index]" :disabled="!isStopEditable(stop)">
                  <template v-if="Boolean(stop.$timeError)" #error>
                    <span class="font-size-12 text-danger">
                      {{ stop.$timeError }}
                    </span>
                  </template>
                </stop-date-range>
                <v-select
                  v-if="stop.type === STOP_DELIVERY"
                  v-model="stop.freights"
                  :disabled="!isStopEditable(stop)"
                  :items="getFreightsForStop(stop, index)"
                  :multiple="true"
                  :rules="[requiredValidator]"
                  chips
                  class="my-3 required"
                  color="primary"
                  density="compact"
                  hide-details="auto"
                  item-title="$name"
                  item-value="id"
                  label="Freight"
                  variant="outlined"
                >
                  <template #chip="{ item, index: freightIndex }">
                    <v-chip
                      :class="getFreightStateClass(item.raw)"
                      :closable="!item.raw.disabled"
                      class="font-weight-500 text-grey-darken-2"
                      close-icon="mdi-close"
                      @click:close="() => stop.freights.splice(freightIndex, 1)"
                    >
                      <span :class="getFreightStateClass(item.raw)" class="freight-dot mr-1">●</span>
                      <span :class="{ 'text-strikethrough text-grey-lighten-1': item.raw.isDeleted }">
                        {{ item.title }}
                      </span>
                    </v-chip>
                  </template>
                  <template #item="{ item, props: itemProps }">
                    <v-list-item
                      :disabled="item.raw.disabled"
                      :label="item.title"
                      :value="item.value"
                      v-bind="itemProps"
                    >
                      <template #prepend="status">
                        <v-checkbox-btn
                          :key="item.value"
                          :color="!item.raw.disabled ? 'primary' : 'grey-darken-3'"
                          :model-value="status.isSelected"
                          :ripple="false"
                          tabindex="-1"
                        />
                      </template>
                      <template #title>
                        <div
                          :class="{
                            'text-grey-darken-3': item.raw.disabled,
                            'text-strikethrough': item.raw.isDeleted,
                          }"
                        >
                          {{ item.title }}
                        </div>
                      </template>
                      <template v-if="typeof item.raw === 'object' && 'suffix' in item.raw" #append>
                        <div class="text-grey-darken-2">{{ item.raw.suffix }}</div>
                      </template>
                    </v-list-item>
                  </template>
                </v-select>
                <div v-if="stop.type === STOP_PICK_UP">
                  <div class="frights-list pt-2">
                    <div v-for="(freight, freightIndex) in getStopFreights(stop)" :key="freight?.id" class="mb-4">
                      <div :class="getFreightStateClass(freight)" class="freight-block">
                        <div class="freight-header">
                          <div class="d-flex align-center mb-2">
                            <div class="text-caption text-grey-darken-1">Freight {{ freight.$number }}:</div>
                            <order-freight-state
                              v-if="order.tripId"
                              :freight="freight"
                              class="ml-2"
                            ></order-freight-state>

                            <v-radio-group
                              v-model="freight.type"
                              :disabled="!isFreightEditable(stop, freight)"
                              :inline="true"
                              class="ml-4"
                              color="primary"
                              density="compact"
                              hide-details
                            >
                              <v-radio :value="freightTypePallets">
                                <template #label>
                                  <span class="text-caption">Pallets</span>
                                </template>
                              </v-radio>
                              <v-radio :value="freightTypePieces" class="ml-4">
                                <template #label>
                                  <span class="text-caption">Pieces</span>
                                </template>
                              </v-radio>
                            </v-radio-group>

                            <div v-if="freight.$error" class="text-red-lighten-2 ml-2" data-qa="alert-message">
                              {{ freight.$error }}
                            </div>
                            <v-spacer />
                            <v-btn
                              v-if="stop.freights.length > 1"
                              :disabled="!isFreightEditable(stop, freight)"
                              class="text-uppercase text-caption text-red-lighten-1 font-weight-600 pr-2 pl-1 add-freight__btn"
                              variant="text"
                              @click="removeFreight(freight)"
                            >
                              <v-icon class="mr-2" color="red-lighten-1" small>mdi-delete-outline</v-icon>
                              Delete freight
                            </v-btn>
                          </div>
                        </div>
                        <div class="freight-body">
                          <div>
                            <div class="d-flex align-center justify-space-between flex-wrap">
                              <v-row class="align-center medium-padding">
                                <v-col lg="10" md="10" sm="10">
                                  <v-row class="medium-padding">
                                    <v-col lg="4" sm="6">
                                      <v-row class="medium-padding">
                                        <v-col sm="6" xl="6">
                                          <v-text-field
                                            v-model="freight.quantity"
                                            :disabled="!isFreightEditable(stop, freight)"
                                            :rules="[requiredValidator, positiveNumber]"
                                            class="required"
                                            color="primary"
                                            data-qa="planned_quantity"
                                            density="compact"
                                            hide-details="auto"
                                            label="Quantity"
                                            required
                                            variant="outlined"
                                          />
                                        </v-col>
                                        <v-col sm="6" xl="6">
                                          <v-text-field
                                            v-model="freight.weight"
                                            :disabled="!isFreightEditable(stop, freight)"
                                            :rules="[requiredValidator, positiveNumber]"
                                            class="required"
                                            color="primary"
                                            data-qa="planned_weight"
                                            density="compact"
                                            hide-details="auto"
                                            label="Weight"
                                            variant="outlined"
                                          />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                    <v-col lg="5" sm="6">
                                      <v-row>
                                        <v-col class="dimension-part">
                                          <v-text-field
                                            v-model="freight.length"
                                            :disabled="!isFreightEditable(stop, freight)"
                                            color="primary"
                                            data-qa="length"
                                            density="compact"
                                            hide-details
                                            label="Length"
                                            variant="outlined"
                                          />
                                        </v-col>
                                        <v-col class="dimension-part">
                                          <v-text-field
                                            v-model="freight.width"
                                            :disabled="!isFreightEditable(stop, freight)"
                                            color="primary"
                                            data-qa="width"
                                            density="compact"
                                            hide-details
                                            label="Width"
                                            variant="outlined"
                                          />
                                        </v-col>
                                        <v-col>
                                          <v-text-field
                                            v-model="freight.height"
                                            :disabled="!isFreightEditable(stop, freight)"
                                            color="primary"
                                            data-qa="height"
                                            density="compact"
                                            hide-details
                                            label="Height"
                                            variant="outlined"
                                          />
                                        </v-col>
                                      </v-row>
                                    </v-col>
                                  </v-row>
                                </v-col>
                                <v-col lg="2" md="2" sm="2">
                                  <div class="d-flex align-center justify-end">
                                    <v-checkbox
                                      v-model="freight.stackable"
                                      :disabled="!isFreightEditable(stop, freight)"
                                      class="pa-0 ma-0 mr-4 stackable"
                                      color="primary"
                                      data-qa="stackable"
                                      hide-details
                                    >
                                      <template #label>
                                        <span class="text-caption">Not stackable</span>
                                      </template>
                                    </v-checkbox>
                                  </div>
                                </v-col>
                              </v-row>
                            </div>
                          </div>

                          <alert-message v-if="!isFreightSelectedOnDelivery(freight)" class="mt-4"
                            >Select the Freight at delivery stop or delete this freight</alert-message
                          >
                        </div>
                      </div>
                      <v-btn
                        v-if="freightIndex === getStopFreights(stop).length - 1"
                        :disabled="!isFreightEditable(stop)"
                        class="text-uppercase text-caption text-primary font-weight-600 pr-2 pl-1 mt-1 add-freight__btn"
                        variant="text"
                        @click="addFreight(stop)"
                      >
                        <v-icon class="mr-2" color="primary" small>mdi-plus</v-icon>
                        Freight
                      </v-btn>
                    </div>
                  </div>

                  <div v-if="stop.freights.length > 1" class="d-flex mb-5">
                    <div class="mr-2 bg-blue-grey-lighten-5 pt-2 pb-2 pr-3 pl-3 rounded-lg total-block">
                      <div class="text-grey-darken-2 text-caption mr-1">
                        Total quantity:<br />
                        <span v-if="getStopPallets(stop) > 0" class="font-weight-medium mr-3">
                          {{ getStopPallets(stop) }} pallets
                        </span>
                        <span v-if="getStopPieces(stop) > 0" class="font-weight-medium">
                          {{ getStopPieces(stop) }} pieces
                        </span>
                      </div>
                    </div>
                    <div class="bg-blue-grey-lighten-5 pt-2 pb-2 pr-3 pl-3 rounded-lg total-block">
                      <div class="text-grey-darken-2 text-caption mr-1">
                        Total weight:
                        <div class="font-weight-medium">{{ getStopWeight(stop) }} lbs</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="stops-right__bottom text-area-wrapper">
                <omni-textarea
                  v-model="stop.note"
                  data-qa="stop-notes"
                  label="Order stop note"
                  rows="3"
                ></omni-textarea>
              </div>
            </v-col>
          </v-row>
          <alert-message
            v-for="deletedFreight in getStopDeletedFreights(stop)"
            :key="deletedFreight.id"
            bg-color="indigo-lighten-5"
            border-color="none"
            class="mt-4"
            color="indigo"
            icon="mdi-information-outline"
            icon-color="indigo"
          >
            Freight {{ deletedFreight.$number }} will be removed after re-dispatch confirmation
          </alert-message>
          <alert-message
            v-if="stop.isDeleted"
            bg-color="indigo-lighten-5"
            border-color="none"
            class="mt-4"
            color="indigo"
            icon="mdi-information-outline"
            icon-color="indigo"
          >
            Stop will be removed after dispatch changes confirmation
          </alert-message>

          <alert-message v-if="isAllFreightDeleted(stop)" class="mt-4">
            Add at least one freight to this stop, or remove the stop
          </alert-message>
        </form-block>
        <div
          v-if="stop.$error"
          class="text-red-lighten-2 mb-8 mt-n2 d-flex align-center"
          data-qa="alert-message-last-pickup"
        >
          <v-icon icon="mdi-alert-octagon-outline mr-2"></v-icon>
          {{ stop.$error }}
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.total-block {
  min-width: 187px;
}

.form-block {
  &.v-field--input {
    border: 1px solid $danger;
  }
}

.freight-block {
  border: 1px solid $grey-light;
  border-radius: 8px;
  padding: 8px 16px;

  &.freight--new {
    border-color: $green;
  }

  &.freight--updated {
    border-color: $warning;
  }
}

.v-chip {
  &.freight--new {
    border: 1px solid $green;
  }

  &.freight--updated {
    border: 1px solid $warning;
  }
}

.freight-dot {
  display: none;

  &.freight--new {
    display: block;
    color: $green;
  }

  &.freight--updated {
    display: block;
    color: $warning;
  }

  &.freight--deleted {
    display: block;
    color: $green-lighten;
  }
}

.dimension-part {
  position: relative;

  &:after {
    content: "х";
    position: absolute;
    top: 50%;
    right: -4px;
    transform: translateY(-50%);
  }
}

.stackable {
  :deep(.v-label) {
    white-space: nowrap;
  }
}

.medium-padding {
  margin: -7px -8px;

  & > [class*="col-"],
  & > .col {
    padding: 7px 8px;
  }
}

.height-full__bottom,
.stops-right__bottom {
  .v-input {
    height: 100%;

    .v-input__control {
      height: 100%;
    }
  }
}

.stops-right__bottom {
  .v-input__slot {
    height: 100%;
  }
}
</style>
